import { Container, Typography, Box, Link, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid2';

function LyricJamSonic() {
    return (
        <Container sx={{ padding: '40px 0' }}>
            <Typography variant="h2" gutterBottom sx={{ textAlign: 'left', marginBottom: '24px' }}>
                LyricJam Sonic: A Generative System for Real-Time Composition and Musical Improvisation
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', marginBottom: '30px', fontStyle: 'italic' }}>
                Published at the 12th International Conference on Artificial Intelligence in Music, Sound, Art and Design (EvoMusArt 2023)
            </Typography>

            {/* Interactive Demo Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Try LyricJam Sonic
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    Experience LyricJam Sonic directly through our web application:
                </Typography>
                <Card sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                        component="img"
                        src="/images/LyricJamSonicMode.jpg"
                        alt="LyricJam Sonic Interface"
                        sx={{
                            width: '100%',
                            height: '300px',
                            objectFit: 'cover',
                        }}
                    />
                    <Box sx={{ 
                        padding: '30px',
                        textAlign: 'center'
                    }}>
                        <Typography variant="h5" sx={{ marginBottom: '15px', fontWeight: 'bold' }}>
                            Live Web Application
                        </Typography>
                        <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                            The system creates continuous soundscapes from over 4,000 ten-second clips of electronic music by "Every Constant Is Obsolete"
                        </Typography>
                        <Link 
                            href="https://lyricjam.ai" 
                            target="_blank" 
                            rel="noopener"
                            className="button-style"
                            sx={{ 
                                fontSize: '1.1rem'
                            }}
                        >
                            Launch LyricJam Sonic
                        </Link>
                    </Box>
                </Card>
            </Box>

            {/* Abstract Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Abstract
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, textAlign: 'justify' }}>
                    Electronic music artists and sound designers have unique workflow practices that necessitate specialized approaches for developing music information retrieval and creativity support tools. Furthermore, electronic music instruments, such as modular synthesizers, have near-infinite possibilities for sound creation and can be combined to create unique and complex audio paths. The process of discovering interesting sounds is often serendipitous and impossible to replicate. For this reason, many musicians in electronic genres record audio output at all times while they work in the studio. Subsequently, it is difficult for artists to rediscover audio segments that might be suitable for use in their compositions from thousands of hours of recordings. In this paper, we describe LyricJam Sonic, a creative tool for musicians to rediscover their previous recordings, re-contextualize them with other recordings, and create original live music compositions in real-time. A bi-modal AI-driven approach uses generated lyric lines to find compatible audio clips from the artist's past studio recordings, and uses them to generate new lyric lines, which in turn are used to find other clips, thus creating a continuous and evolving stream of music and lyrics. The intent is to keep the artists in a state of creative flow conducive to music creation rather than taking them into an analytical/critical state of deliberately searching for past audio segments. The system can run in either a fully autonomous mode without user input, or in a live performance mode, where the artist plays live music, while the system "listens" and creates a continuous stream of music and lyrics in response.
                </Typography>
            </Box>

            {/* Key Features Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Key Innovation
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Serendipitous Discovery
                                </Typography>
                                <Typography variant="body2">
                                    Helps electronic music artists rediscover sounds from their vast catalogue of studio recordings without interrupting their creative flow. The system facilitates unexpected connections between past recordings and current compositions.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Bi-Modal AI Architecture
                                </Typography>
                                <Typography variant="body2">
                                    Uses a unique combination of neural networks: a Spectrogram VAE for audio analysis, a Text-CVAE for lyric generation, and a GAN for predicting the next audio clip based on both audio and lyric features.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Dual Operating Modes
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Autonomous Mode:</strong> Creates continuous compositions without user input, ideal for studio inspiration.<br/>
                                    <strong>Live Performance Mode:</strong> Responds to live music input, creating real-time accompaniment for performances.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Self-Perpetuating System
                                </Typography>
                                <Typography variant="body2">
                                    Generated lyrics influence the selection of the next audio clip, which in turn influences the generation of subsequent lyrics, creating an evolving and coherent musical narrative.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Technical Approach Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Technical Approach
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    LyricJam Sonic consists of four main neural network components working in concert:
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                    Spectrogram VAE (Spec-VAE)
                                </Typography>
                                <Typography variant="body2">
                                    Learns latent representations of audio clips by converting raw waveforms into Mel-spectrograms and encoding them into a continuous latent space for similarity matching and retrieval.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                    Text Conditional VAE (Text-CVAE)
                                </Typography>
                                <Typography variant="body2">
                                    Generates lyric lines conditioned on audio clips using LSTM networks, ensuring that generated lyrics are emotionally and stylistically congruent with the accompanying music.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                                    Generative Adversarial Network (GAN)
                                </Typography>
                                <Typography variant="body2">
                                    Predicts the latent code of the next audio clip based on the element-wise addition of the previous audio clip's latent code and the generated lyric line's latent code.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                                    Retrieval Module
                                </Typography>
                                <Typography variant="body2">
                                    Finds the most similar audio clip from the artist's collection using cosine similarity between the GAN-predicted latent code and pre-computed latent codes of all available clips.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Slides Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Conference Presentation
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    View the presentation slides from EvoMusArt 2023:
                </Typography>
                <Box sx={{
                    width: '100%',
                    height: '600px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <iframe
                        src="/pdf/LyricJam-Sonic-slides.pdf"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="LyricJam Sonic Presentation Slides"
                    />
                </Box>
            </Box>

            {/* Paper Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Research Paper
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    Read the full research paper:
                </Typography>
                <Box sx={{
                    width: '100%',
                    height: '800px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <iframe
                        src="/pdf/LyricJam-Sonic.pdf"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="LyricJam Sonic Research Paper"
                    />
                </Box>
            </Box>

            {/* Links Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Resources
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="https://lyricjam.ai"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Web Application
                                    </Typography>
                                    <Typography variant="body2">
                                        Try LyricJam Sonic online
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="/pdf/LyricJam-Sonic-slides.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Slides
                                    </Typography>
                                    <Typography variant="body2">
                                        Presentation from EvoMusArt 2023
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="/pdf/LyricJam-Sonic.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        PDF version of the research paper
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Link
                            href="https://arxiv.org/pdf/2210.15638"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
                        >
                            <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        ArXiv Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        Pre-print version
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            {/* Citation Section */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Citation
                </Typography>
                <Box sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '20px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        @inproceedings{'{'}vechtomova2023lyricjam,<br />
                        &nbsp;&nbsp;title={'{'}LyricJam Sonic: A Generative System for Real-Time Composition and Musical Improvisation{'}'},<br />
                        &nbsp;&nbsp;author={'{'}Vechtomova, Olga and Sahu, Gaurav{'}'},<br />
                        &nbsp;&nbsp;booktitle={'{'}Artificial Intelligence in Music, Sound, Art and Design{'}'},<br />
                        &nbsp;&nbsp;pages={'{'}294--309{'}'},<br />
                        &nbsp;&nbsp;year={'{'}2023{'}'},<br />
                        &nbsp;&nbsp;publisher={'{'}Springer{'}'}<br />
                        {'}'}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
}

export default LyricJamSonic;